import { useState, useCallback, useContext, createContext, useMemo } from 'react';

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    const newToast = { 
      id, 
      type: 'info',
      duration: 5000,
      ...toast 
    };
    
    setToasts(prev => [...prev, newToast]);
    
    // Auto remove after duration
    if (newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
    }

    return id;
  }, [removeToast]);

  const showSuccess = useCallback((title, message) => {
    return showToast({
      type: 'success',
      title,
      message,
    });
  }, [showToast]);

  const showError = useCallback((title, message) => {
    return showToast({
      type: 'error',
      title,
      message,
      duration: 7000, // Errors stay longer
    });
  }, [showToast]);

  const showWarning = useCallback((title, message) => {
    return showToast({
      type: 'warning',
      title,
      message,
    });
  }, [showToast]);

  const showInfo = useCallback((title, message) => {
    return showToast({
      type: 'info',
      title,
      message,
    });
  }, [showToast]);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  const value = useMemo(() => ({
    toasts,
    showToast,
    removeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    clearAll
  }), [toasts, showToast, removeToast, showSuccess, showError, showWarning, showInfo, clearAll]);

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error('useToast must be used within a ToastProvider');
    }

    return {
      toasts: [],
      showToast: () => 0,
      removeToast: () => {},
      showSuccess: () => 0,
      showError: () => 0,
      showWarning: () => 0,
      showInfo: () => 0,
      clearAll: () => {}
    };
  }

  return context;
};
